import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HeaderText2TW } from 'tailwindComponents/HeaderTextTW/HeaderTextTW';
import { filterDisbursementsByType } from './util/filterDisbursementsByType';
import { Currency, Spinner } from 'ui-toolkit';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import { IDisburseAllDisbursementData } from 'types/order/escrow/IDisburseAllDisbursementData';
import { Dropdown } from 'tailwindComponents/dropdown/Dropdown';
import { DocumentDetails } from '../edit/documents/DocumentDetails';
import { AlertBannerTW } from 'tailwindComponents/alertBannerTW/AlertBannerTW';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { sortByCreatedAtDescending } from '../SortByCreatedAt';
import { createDisbursementColumns } from './createDisbursementColumns';
import { AllDisbursementTables } from './AllDisbursmentTables';
import { PrintChecksStep } from './PrintChecksStep';
import { IDisburseAllRequest } from 'types/order/escrow/IDisburseAllRequest';
import { useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useGetTotals } from './useGetTotals';
import { CheckForAvailableFundsModal } from 'components/order/escrow/escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { LinkTW } from 'tailwindComponents/link/LinkTW';
import { clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { assertDefined } from 'utilities/Assertions';

interface IDisbursementModal {
  orderId: string;
  activeCategories: IDocumentCategory[];
  disbursements: IDisburseAllDisbursementData[];
  documentsList: IStandardDocumentVersionData[];
  isLoadingDocuments: boolean;
  onDisburseAll: (x: IDisburseAllRequest) => Promise<void>;
  disburseAllIsLoading: boolean;
}

export const DisbursementsModal: React.FC<IDisbursementModal> = ({
  orderId,
  activeCategories,
  disbursements,
  documentsList,
  isLoadingDocuments,
  onDisburseAll,
  disburseAllIsLoading,
}) => {
  const [excludedDisbursements, setExcludedDisbursements] = useState<string[]>([]);
  const onToggleClick = useCallback(
    (disbursementId: string) => {
      setExcludedDisbursements((prevState) => {
        if (prevState.includes(disbursementId)) {
          return prevState.filter((item) => item !== disbursementId);
        }
        return [...prevState, disbursementId];
      });
    },
    [setExcludedDisbursements]
  );
  const filteredDisbursements = useMemo(
    () => filterDisbursementsByType(disbursements, excludedDisbursements, onToggleClick),
    [disbursements, excludedDisbursements, onToggleClick]
  );
  const [selectedDisbursement, setSelectedDisbursement] = useState<IDisburseAllDisbursementData | undefined>();

  const selectedDocuments = useMemo(() => {
    return (
      selectedDisbursement?.fee.linkedDocumentIds
        .map((t) => documentsList.find((doc) => doc.id === t))
        .filter((t) => t)
        .map((t) => t!)
        .toSorted(sortByCreatedAtDescending) ?? []
    );
  }, [documentsList, selectedDisbursement]);

  const [pdfToPreview, setPdfToPreview] = useState<string | undefined>(selectedDocuments[0]?.fileName);

  useEffect(() => {
    setPdfToPreview(selectedDocuments[0]?.fileName);
  }, [selectedDocuments, setPdfToPreview]);

  const previewingDocument = useMemo(
    () => selectedDocuments.find((doc) => doc.fileName === pdfToPreview),
    [pdfToPreview, selectedDocuments]
  );

  const getColumns = useMemo(() => createDisbursementColumns(true, setSelectedDisbursement), [setSelectedDisbursement]);

  const { data: escrowData } = useGetDetailedEscrowDataQuery(orderId);
  const escrowFileId = escrowData?.id;
  const handleDisburseAll = useCallback(async () => {
    assertDefined(escrowFileId, 'Escrow File ID');
    const disburseAllRequest: IDisburseAllRequest = {
      orderId,
      escrowId: escrowFileId,
      achIds: filteredDisbursements.ACH.filter((x) => x.enabled).map((y) => y.disbursement.id),
      manualCheckIds: filteredDisbursements.Check.filter((x) => x.enabled).map((y) => y.disbursement.id),
      wireIds: filteredDisbursements.Wire.filter((x) => x.enabled).map((y) => y.disbursement.id),
      internalTransferIds: filteredDisbursements['Internal Transfer']
        .filter((x) => x.enabled)
        .map((y) => y.disbursement.id),
    };
    onDisburseAll(disburseAllRequest);
  }, [onDisburseAll, filteredDisbursements, orderId, escrowFileId]);

  const { total, available, isEnough } = useGetTotals(filteredDisbursements);
  const [showNotEnoughWarning, setShowNotEnoughWarning] = useState<boolean>(false);
  const onStartSendProcess = useCallback(() => {
    if (isEnough) {
      handleDisburseAll();
    } else {
      setShowNotEnoughWarning(true);
    }
  }, [isEnough, setShowNotEnoughWarning, handleDisburseAll]);

  const onIgnoreWarning = useCallback(() => {
    setShowNotEnoughWarning(false);
    handleDisburseAll();
  }, [handleDisburseAll, setShowNotEnoughWarning]);

  return (
    <>
      <PrintChecksStep />
      <div className={'relative z-10 bg-gray-100 p-4'}>
        <section className="flex-col gap-2">
          <HeaderText2TW>Disburse All Staged Fees</HeaderText2TW>
          <section className="text-base font-normal not-italic">
            Please note: any staged disbursements labeled as Holdback will not be disbursed.
          </section>
        </section>

        <section className="flex flex-row gap-4 pb-16">
          <div className=" flex-shrink flex-grow basis-1/2">
            <div className="sticky top-4 flex flex-row flex-wrap gap-2">
              <AllDisbursementTables
                columns={getColumns}
                data={filteredDisbursements}
                selectedId={selectedDisbursement?.disbursement.id}
                setSelectedDisbursement={setSelectedDisbursement}
              />
            </div>
          </div>

          {/* documents viewer TBD */}
          <div className=" flex-shrink flex-grow basis-1/2">
            <div className="sticky top-4 flex flex-col gap-4">
              <AlertBannerTW>
                Confirm that all checks have printed correctly before closing this modal.
                <br /> Checks are not live until clicking 'Confirm and Send' and should be shredded if cancelled.
              </AlertBannerTW>

              {isLoadingDocuments ? (
                <Spinner />
              ) : (
                <>
                  {!selectedDocuments.length && (
                    <div className="flex w-full flex-row items-center gap-4">
                      <div className="text-xl font-semibold">No documents associated to the selected disbursement.</div>
                    </div>
                  )}

                  {!!selectedDocuments.length && (
                    <>
                      <div className="flex w-full flex-row items-center gap-4">
                        <div className="text-xl font-semibold">Documents</div>

                        <div data-test-id={'dropdown'}>
                          <Dropdown
                            onSelect={setPdfToPreview}
                            options={selectedDocuments.map((t) => t.fileName)}
                            option={pdfToPreview ?? ''}
                            title={''}
                            // eslint-disable-next-line react/style-prop-object
                            style={'document'}
                          />
                        </div>
                      </div>
                      <div className="w-full pl-1 pt-2">
                        {previewingDocument && (
                          <DocumentDetails
                            key={previewingDocument.id}
                            categories={activeCategories}
                            onClose={() => setPdfToPreview(undefined)}
                            pdfPreviewData={previewingDocument}
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
        <section className="h-18 fixed bottom-0 right-0 p-4 backdrop-blur">
          <div className="flex flex-col items-center justify-end gap-2">
            <div className={' grid grid-cols-2 text-right font-semibold'}>
              <div>Total Disbursements:</div>
              <div>
                <Currency value={total} valueIsCents />
              </div>
              <div>Available Funds:</div>
              <div>
                <Currency value={available} />
              </div>
            </div>
            <div className={'flex justify-end gap-2 self-end'}>
              <LinkTW
                data-test-id="close-button"
                type="button"
                secondary
                isButton
                to={clientUrls.urlFor.feesTab(orderId, 'a-j')}
              >
                Cancel
              </LinkTW>

              <ButtonTW
                type="button"
                data-test-id={'confirm-button'}
                onClick={onStartSendProcess}
                disabled={disburseAllIsLoading}
                error={!isEnough}
              >
                Confirm and Send
              </ButtonTW>
            </div>
          </div>
        </section>
      </div>
      {showNotEnoughWarning && (
        <CheckForAvailableFundsModal
          submitData={onIgnoreWarning}
          type={'wire'}
          handleClose={() => setShowNotEnoughWarning(false)}
        />
      )}
    </>
  );
};
